import { useState, useEffect } from "react";
import { createCartCheckout } from "@/helpers/cartHelpers";
import { addToBag, pushAddToCartEvent } from "@/helpers/cartEventHelper";
import { useRouter } from "next/router";
import { useSelector } from "react-redux";
import { generateMultiPassURL } from "../userHelpers";
import { setEstimatedShip, setGroupShipDate } from "../checkout-shipping/checkoutShippingHelper";
import useMediaQuery from "@/components/common/UseMediaQuery/UseMediaQuery";

const useBuyNow = (selectedVariant, isSizeSelected, isSizeDropdownOpen, setIsSizeDropdownOpen, badgeCurated, product, engravingAdded) => {
  const [loading, setLoading] = useState(false); // State to track if the button is in a loading state
  const [toBeBought, setToBeBought] = useState(false); // Flag to track if we need to retry Buy Now after size selection
  const router = useRouter(); // Router instance for navigating to the checkout page
  const logUser = useSelector((state) => state.loginUser); // Retrieve logged-in user details from Redux state
  const buyerIdentity = {}; // Placeholder object for buyer identity (can be extended in the future)
  const isIncognito = false; // Flag to check if the user is browsing in incognito mode
  const isMobile = useMediaQuery("(max-width: 767px)");
  const isPWAApp = useSelector((state) => state.pwa.is_PWA);

  const handleBuyNow = async () => {
    if (!selectedVariant) return; // Exit function if no product variant is selected

    if (!isSizeSelected) {
      setIsSizeDropdownOpen(true); // Open size dropdown if no size has been chosen
      setToBeBought(true); // Mark as waiting for size selection
      return;
    }

    await proceedToBuyNow(selectedVariant);
  };

  const proceedToBuyNow = async (variant) => {
    setLoading(true); // Set loading state to true while processing

    try {
      const extraAtt = {
        engravingAdded: engravingAdded || false, // Include engraving information if available
        isPWAApp:isPWAApp,
      }

      const response = await addToBag(variant, badgeCurated, product, extraAtt,true); // Add product to cart using the provided variant and additional attributes

      if (response) {
        const data = JSON.parse(localStorage.getItem("pdp_page_view")) || {}; // Retrieve stored PDP page view data
        pushAddToCartEvent(data, variant, logUser, window.location.href, "PDP - Buy now"); // Log 'Add to Cart' event for analytics tracking
      }

      const checkoutResponse = await createCartCheckout(response, {}, buyerIdentity, isIncognito, true); 

        if (checkoutResponse?.checkoutUrl) {
            // If user is logged in, use Multipass for seamless Shopify login
            if (logUser?.user_loggedIn?.accessToken) {
                try{
                    const multiPassURL = await generateMultiPassURL({
                        token: logUser.user_loggedIn.accessToken,
                        url: checkoutResponse.checkoutUrl
                    });
    
                    if (multiPassURL.url) {
                        router.push(multiPassURL.url);
                        return;
                    }
                }catch(err){
                    console.error("Error in generating Multipass URL", err); // Handle errors gracefully
                }
            }

            // If user is not logged in, proceed with the normal checkout URL
            router.push(checkoutResponse.checkoutUrl);
        }

    } catch (error) {
      console.error("Error in Buy Now process", error); // Handle errors gracefully
    }

    setLoading(false); // Reset loading state after processing is complete
  };

  // Once the user selects a size, then trigger Buy Now with that variant without extra click
  useEffect(() => {
    if (toBeBought) {
      // Once size dropdown is closed, proceed to Buy Now if a size is selected
      if (!isSizeDropdownOpen) {
        if (isSizeSelected) {
          setToBeBought(false);
          proceedToBuyNow(selectedVariant);
        } 
        // If user closes the size dropdown without selecting a size
        else {
          setToBeBought(false);
        }
      }
    }
  }, [isSizeSelected, isSizeDropdownOpen, selectedVariant]);

  return { handleBuyNow, loading }; // Return the function and loading state to the component
};

export default useBuyNow;
